import { api, unwrap, type Schemas } from '../api/client';
import { applyScheme, saveCustom, type CustomScheme } from './schemes';
import { applyTheme, type Theme } from './theme';

// The color scheme follows a signed-in user from device to device: it is saved to their account
// (/v1/me/preferences) and loaded after sign-in. localStorage stays the fast local copy, so the
// page paints in the right colors before the API answers. Development sign-in has no user, so
// there the scheme stays in the browser only.

/** Fired after the account's scheme has been applied, so an open Layout tab can follow. */
export const SCHEME_LOADED = 'mybooks:scheme-loaded';

export async function loadSavedScheme(): Promise<void> {
  try {
    const saved = (await unwrap(api.GET('/v1/me/preferences'))).data;
    if (!saved || (!saved.color_scheme && !saved.custom_scheme && !saved.theme)) return;
    if (saved.theme) applyTheme(saved.theme as Theme);
    if (saved.custom_scheme) saveCustom({ menu: 0, ...(saved.custom_scheme as Partial<CustomScheme>) } as CustomScheme);
    if (saved.color_scheme) applyScheme(saved.color_scheme);
    window.dispatchEvent(new Event(SCHEME_LOADED));
  } catch {
    // Keep this browser's copy; the next change saves again.
  }
}

export type SaveState = 'saving' | 'saved' | 'failed';

/** Saves light, dark or follow-the-device to the account straight away. Failure keeps this browser's copy. */
export function saveThemeToAccount(theme: Theme): Promise<void> {
  return unwrap(api.PATCH('/v1/me/preferences', { body: { theme } })).then(() => undefined, () => undefined);
}
let timer: ReturnType<typeof setTimeout> | undefined;

/** Saves the scheme to the account after the sliders settle (600 ms), reporting progress. */
export function saveSchemeToAccount(scheme: string, custom: CustomScheme, onState: (state: SaveState) => void, delay = 600) {
  clearTimeout(timer);
  onState('saving');
  timer = setTimeout(() => {
    unwrap(api.PATCH('/v1/me/preferences', { body: { color_scheme: scheme as 'custom', custom_scheme: custom as Schemas['CustomScheme'] } })).then(
      () => onState('saved'),
      () => onState('failed'),
    );
  }, delay);
}
